/**
 * s2k-i18nInfoHelper.js
 * @author: Jiawen Li
 *
 * Description: The Helper functions of i18info.
 *
 * Copyright (c) 2023 Amazon.com, Inc. All rights reserved.
 */

import {getAuthRelatedUrls} from "./s2k-request";

/**
 * Code of current support counties
 * @typedef {"au" | "br" | "ca" | "fr" | "de" | "in" | "it" | "jp" | "mx" | "nl" | "es" | "uk" | "us"} CountryCode
 */

/**
 * List of code of current support counties
 * @type {Array<CountryCode>}
 */
const supportCountryCodes = ["au", "br", "ca", "fr", "de", "in", "it", "jp", "mx", "nl", "es", "uk", "us"];

/**
 * Return List of code of current support counties
 * @return {Array<CountryCode>} List of code in of current support counties
 */
function getSupportCountryCodeList() {
    return supportCountryCodes;
}

/**
 * Based on the country code passed by caller, return the full name of country
 * @param {CountryCode} code The locale code of country
 * @return {string} The full name of country
 */
function getCountryName(code) {
    let prefix = "popUpCountry";
    return chrome.i18n.getMessage(prefix + code.toUpperCase());
}

/**
 * Based on the country code passed by caller, return the Path of the icon of the country
 * @param {CountryCode} code The locale code of country
 * @return {string} The Path of the icon of the country
 */
function getCountryIconPath(code) {
    let prefix = "/images/country/s2k-popup-icon-";
    return prefix + code + '.svg';
}

/**
 * Return the BaseUrl of current country
 * @return {Promise<string>} The BaseUrl of current country based on package mode
 */
async function getDomain() {
    let packageMode = BUILD_PROPS.__mode__;
    let code = await getCurrentCountryCode();
    switch (code) {
        case "au":
            if (packageMode === "development") {
                return "https://au-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.com.au";
            }
            break;
        case "br":
            if (packageMode === "development") {
                return "https://br-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.com.br";
            }
            break;
        case "ca":
            if (packageMode === "development") {
                return "https://ca-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.ca";
            }
            break;
        case "de":
            if (packageMode === "development") {
                return "https://de-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.de";
            }
            break;
        case "es":
            if (packageMode === "development") {
                return "https://es-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.es";
            }
            break;
        case "fr":
            if (packageMode === "development") {
                return "https://fr-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.fr";
            }
            break;
        case "in":
            if (packageMode === "development") {
                return "https://in-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.in";
            }
            break;
        case "it":
            if (packageMode === "development") {
                return "https://it-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.it";
            }
            break;
        case "jp":
            if (packageMode === "development") {
                return "https://jp-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.co.jp";
            }
            break;
        case "mx":
            if (packageMode === "development") {
                return "https://mx-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.com.mx";
            }
            break;
        case "nl":
            if (packageMode === "development") {
                return "https://nl-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.nl";
            }
            break;
        case "uk":
            if (packageMode === "development") {
                return "https://uk-pre-prod.amazon.com";
            } else {
                return "https://www.amazon.co.uk";
            }
            break;
        case "us":
            if (packageMode === "development") {
                return "https://pre-prod.amazon.com";
            } else {
                return "https://www.amazon.com";
            }
            break;
    }
}

/**
 * Get current country code from cache
 * @return {Promise< CountryCode >} sendResponse A callback func to pass the result to the caller
 */
async function getCurrentCountryCode() {
    let result = await chrome.storage.local.get(["currentCountryCode"]);
    if (result.currentCountryCode === undefined) {
        // Set default country code to us
        const currentCountryCode = "us";
        // save it to local storage
        await setCountryCode(currentCountryCode);
        // Pass the current country code to caller
        return currentCountryCode;
    } else {
        // Pass the current country code to caller if it is existed
        return result.currentCountryCode;
    }
}

/**
 * Set current selected country code to cache
 * @param {CountryCode} currentCountryCode Locale code of current selected country
 * @return { Promise<{ registerUrl: string, signInUrl: string, passwordUrl: string }> } Auth related urls
 */
async function setCountryCode(currentCountryCode) {
    await chrome.storage.local.set({"currentCountryCode": currentCountryCode});
    // Request new auth related urls after change country code.
    let res = await getAuthRelatedUrls(false);
    return res.data;
}

export {
    getSupportCountryCodeList,
    getCountryName,
    getCountryIconPath,
    getDomain,
    setCountryCode,
    getCurrentCountryCode
};